import produce from 'immer';
import { useEffect, useRef, useState } from 'react';
import { get, post } from '.';

const parseRequestArguments = (requestArguments, fallbackUrl, onAbort) => produce(requestArguments, (draftRequestArguments) => {
    if (typeof draftRequestArguments[0] !== 'string') {
        const [requestData] = draftRequestArguments;

        draftRequestArguments[1] = requestData;
        draftRequestArguments[0] = fallbackUrl;
    }

    if (!draftRequestArguments[2]) {
        draftRequestArguments[2] = onAbort;
    }
});

export const useFetch = (urlParameter, dataParameter, dependencies) => {
    const abortControllerRef = useRef();
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    const [isPending, setIsPending] = useState(false);

    const saveAbortCallback = (callback) => {
        abortControllerRef.current = callback;
    };

    const handleSuccess = (requestResponse) => {
        setResponse(requestResponse);
        setIsPending(false);
        saveAbortCallback(null);
    };

    const handleError = (requestError) => {
        // We want to execute next only if request wasn't aborted
        if (requestError.name !== 'AbortError') {
            setError(requestError);
            setIsPending(false);
            saveAbortCallback(null);
        }
    };

    const getRequestHandlerForMethod = method => (url, data, options) => {
        const onAbort = abortCallback => abortCallback;
        const promise = method(...parseRequestArguments([url, data, options], urlParameter, onAbort));

        if (error) {
            setError(null);
        }

        setIsPending(true);
        saveAbortCallback(onAbort);

        promise
            .catch(handleError)
            .then(handleSuccess);

        return promise;
    };

    const handleAbortRequest = () => {
        const { current } = abortControllerRef;

        if (current) {
            current();
        }
    };

    const getHandler = getRequestHandlerForMethod(get);
    const postHandler = getRequestHandlerForMethod(post);

    useEffect(() => {
        if (dependencies) {
            getHandler(dataParameter);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies || []);

    useEffect(() => () => {
        handleAbortRequest();
    }, []);

    return {
        error,
        get: getHandler,
        post: postHandler,
        response,
        isPending,
        onAbort: handleAbortRequest
    };
};
